import { useState, useEffect } from "react";
import { Icon } from "../Icon";
import { usePlatformConfig } from "../../utils/platformConfig";
import { LANGUAGE_CHOICES, languageName } from "../../utils/listChoices";
import { useLanguage } from "../../context/LanguageContext";

/* The building blocks of the onboarding forms - the Alting driver form and the cab owner
   form both draw from here, so the two look and behave the same and a change to one is
   a change to both.

   Built for someone who reads slowly, or reads their own language first: one plain label
   per field with a picture beside it, big tap targets, a tick when a thing is done, and
   "Optional" said out loud on the few fields that are - instead of a red star on the
   rest. */

/* ── Small drawings the icon set does not have ─────────────────────────── */
export function SteeringIcon({ size = 16, color = "#5E8700" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="2.2" />
      <path d="M3.3 10.2c3.3.6 5.5 1 6.6 1.3M20.7 10.2c-3.3.6-5.5 1-6.6 1.3M12 14.2V21" />
    </svg>
  );
}
export function PaletteIcon({ size = 16, color = "#6B7280" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3a9 9 0 1 0 0 18c1 0 1.6-.8 1.6-1.7 0-.5-.2-.9-.5-1.2-.3-.3-.5-.7-.5-1.2 0-.9.7-1.6 1.6-1.6H16a5 5 0 0 0 5-5c0-4.2-4-7.3-9-7.3z" />
      <circle cx="7.5" cy="11.5" r="1" /><circle cx="9.5" cy="7.5" r="1" /><circle cx="14.5" cy="7.5" r="1" /><circle cx="17" cy="11" r="1" />
    </svg>
  );
}
function IndiaFlag() {
  return (
    <svg width="22" height="15" viewBox="0 0 22 15" aria-hidden="true" className="rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.08)]">
      <rect width="22" height="5" fill="#FF9933" /><rect y="5" width="22" height="5" fill="#FFFFFF" /><rect y="10" width="22" height="5" fill="#138808" />
      <circle cx="11" cy="7.5" r="1.8" fill="none" stroke="#000080" strokeWidth="0.6" />
    </svg>
  );
}

/* ── Field ───────────────────────────────────────────────────────────── */
export function Field({ label, children, hint, optional, optionalText, error, icon, sub }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        {icon && <span className="shrink-0">{typeof icon === "string" ? <Icon name={icon} size={15} stroke="#5E8700" /> : icon}</span>}
        <label className="text-[12px] font-semibold text-[#374151]">{label}</label>
        {optional && (
          <span className="text-[9px] font-semibold text-[#6B7280] bg-[#F3F4F6] rounded-full px-1.5 py-[1px]">{optionalText}</span>
        )}
      </div>
      {sub && <p className="text-[10px] text-[#6B7280] -mt-0.5 mb-1.5">{sub}</p>}
      {children}
      {error && (
        <p className="flex items-start gap-1 text-[10px] font-medium text-[#DC3545] mt-1">
          <Icon name="alertTri" size={12} stroke="#DC3545" className="shrink-0 mt-[1px]" /> <span>{error}</span>
        </p>
      )}
      {hint && !error && <p className="text-[10px] text-[#6B7280] mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

/* An input with a small picture inside it, on the left - the reader sees what goes in
   the box before reading the label. */
export function IconInput({ icon, inputRef, className = "", bad, ...props }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
        {typeof icon === "string" ? <Icon name={icon} size={16} stroke="#9CA3AF" /> : icon}
      </span>
      <input ref={inputRef} {...props} className={`input !pl-10 ${bad ? "border-[#DC3545]" : ""} ${className}`} />
    </div>
  );
}

/* ── Section ─────────────────────────────────────────────────────────────
   One numbered block of the form. The number turns into a tick when everything in it is
   filled, so progress is visible without reading a word. */
export function Section({ id, n, icon, title, help, done, doneText, sectionRef, children, className = "" }) {
  return (
    <section id={id} ref={sectionRef}
      className={`bg-white rounded-[18px] border border-[#EAEFF5] shadow-[0_2px_10px_rgba(15,23,42,0.04)] p-4 space-y-3.5 scroll-mt-[64px] ${className}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-[14px] font-extrabold ${
          done ? "bg-[#7BAE00] text-white" : "bg-[#F1F7E3] text-[#5E8700]"}`}>
          {done ? <Icon name="check" size={17} stroke="white" strokeWidth={3} /> : n}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {icon && <span className="shrink-0">{typeof icon === "string" ? <Icon name={icon} size={18} stroke="#5E8700" /> : icon}</span>}
            <h2 className="text-[15px] font-extrabold text-[#1F2937] leading-tight">{title}</h2>
            {done && <span className="ml-auto text-[9px] font-bold text-[#5E8700] bg-[#F6FAEC] rounded-full px-2 py-0.5 shrink-0">{doneText}</span>}
          </div>
          {help && <p className="text-[11px] text-[#6B7280] leading-snug mt-0.5">{help}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

/* An optional section: an icon instead of a number, no tick, "Optional" beside the title. */
export function OptionalSection({ icon, title, help, optionalText, children }) {
  return (
    <section className="bg-white rounded-[18px] border border-[#EAEFF5] shadow-[0_2px_10px_rgba(15,23,42,0.04)] p-4 space-y-3.5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-[#F3F4F6]">
          <Icon name={icon} size={16} stroke="#6B7280" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-extrabold text-[#1F2937] leading-tight">{title}</h2>
            <span className="text-[9px] font-semibold text-[#6B7280] bg-[#F3F4F6] rounded-full px-1.5 py-[1px]">{optionalText}</span>
          </div>
          {help && <p className="text-[11px] text-[#6B7280] leading-snug mt-0.5">{help}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

/* Which sections the partner has actually had on screen.
   Some sections arrive with their answers already filled in - every day of the week,
   all three trip types, the city's rates - so the submit's own rules pass before anyone
   has looked at them, and the step row ticked them on the first screen. A section now
   counts as done only once it has also been seen: any part of it in the top two-thirds
   of the screen. Once seen, it stays seen. A browser without IntersectionObserver
   counts everything as seen, which is how it behaved before. */
export function useSeenSections(sectionRefs) {
  const [seen, setSeen] = useState({});
  useEffect(() => {
    const entries = Object.entries(sectionRefs).filter(([, ref]) => ref?.current);
    if (typeof IntersectionObserver === "undefined") {
      setSeen(Object.fromEntries(entries.map(([key]) => [key, true])));
      return undefined;
    }
    const keyOf = new Map(entries.map(([key, ref]) => [ref.current, key]));
    const io = new IntersectionObserver((changes) => {
      const hits = changes.filter((c) => c.isIntersecting).map((c) => keyOf.get(c.target)).filter(Boolean);
      if (hits.length === 0) return;
      setSeen((prev) => {
        if (hits.every((key) => prev[key])) return prev;
        const next = { ...prev };
        for (const key of hits) next[key] = true;
        return next;
      });
    }, { rootMargin: "0px 0px -35% 0px", threshold: 0 });
    keyOf.forEach((_, el) => io.observe(el));
    return () => io.disconnect();
    // The refs are stable; the sections are all mounted with the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return seen;
}

/* ── Choices ─────────────────────────────────────────────────────────── */
/* A big rounded choice. `on` gives it a green edge and tint with a tick. */
export function Pill({ on, onClick, children, sub, center, icon, className = "" }) {
  return (
    <button type="button" onClick={onClick}
      className={`min-h-[40px] px-3.5 py-2 rounded-full border-2 text-left transition-all active:scale-[0.97] ${
        on ? "bg-[#F1F7E3] border-[#7BAE00] text-[#1F2937]" : "bg-white border-[#E5E7EB] text-[#374151] hover:border-[#7BAE00]/60"
      } ${className}`}>
      <span className={`flex items-center gap-1.5 ${center ? "justify-center" : ""}`}>
        {on ? <Icon name="check" size={14} stroke="#5E8700" strokeWidth={3} /> : icon ? <Icon name={icon} size={14} stroke="#6B7280" /> : null}
        <span className="text-[12px] font-semibold leading-tight">{children}</span>
      </span>
      {sub && <span className="block text-[9px] leading-tight mt-0.5 text-[#6B7280]">{sub}</span>}
    </button>
  );
}

/* A full-width choice row with a tick box - for lists of options that each need a line. */
export function ChoiceRow({ on, onClick, icon, title, sub }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition-all active:scale-[0.99] ${
        on ? "border-[#7BAE00] bg-[#F6FAEC]" : "border-[#E5E7EB] bg-white"}`}>
      <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${on ? "bg-[#7BAE00] border-[#7BAE00]" : "bg-white border-[#CBD5E1]"}`}>
        {on && <Icon name="check" size={14} stroke="white" strokeWidth={3} />}
      </span>
      {icon && <span className="shrink-0">{typeof icon === "string" ? <Icon name={icon} size={16} stroke="#5E8700" /> : icon}</span>}
      <span className="min-w-0">
        <span className="block text-[13px] font-bold text-[#1F2937]">{title}</span>
        {sub && <span className="block text-[10px] text-[#6B7280]">{sub}</span>}
      </span>
    </button>
  );
}

/* Languages as tap-to-pick pills, each in its own script. Stored as before - "Telugu,
   Hindi" - so nothing downstream changes. Another language is added through "Add
   another", which opens a small box; one typed in shows as a picked pill too.
   The list itself lives in utils/listChoices, which the home lists also show
   languages from, so a driver's languages read the same everywhere. */
export { LANGUAGE_CHOICES };
export function LanguagePills({ value, onChange, placeholder, addText }) {
  const { lang } = useLanguage();
  const [other, setOther] = useState("");
  const [adding, setAdding] = useState(false);
  const picked = String(value || "").split(",").map((l) => l.trim()).filter(Boolean);
  const has = (v) => picked.some((p) => p.toLowerCase() === v.toLowerCase());
  const toggle = (v) => onChange((has(v) ? picked.filter((p) => p.toLowerCase() !== v.toLowerCase()) : [...picked, v]).join(", "));
  const extras = picked.filter((p) => !LANGUAGE_CHOICES.some((c) => c.value.toLowerCase() === p.toLowerCase()));
  const addOther = () => {
    const v = other.trim();
    if (v && !has(v)) onChange([...picked, v].join(", "));
    setOther("");
    setAdding(false);
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {LANGUAGE_CHOICES.map((c) => (
          <Pill key={c.value} on={has(c.value)} onClick={() => toggle(c.value)}>
            {lang && lang !== "en" ? languageName(c.value, lang)
              : <>{c.label}{c.label !== c.value && <span className="opacity-70 font-medium"> · {c.value}</span>}</>}
          </Pill>
        ))}
        {extras.map((x) => <Pill key={x} on onClick={() => toggle(x)}>{x}</Pill>)}
        {!adding && (
          <button type="button" onClick={() => setAdding(true)}
            className="min-h-[40px] px-3.5 rounded-full border-2 border-dashed border-[#CBD5E1] text-[12px] font-semibold text-[#374151] flex items-center gap-1.5">
            <Icon name="plus" size={14} stroke="#374151" /> {addText}
          </button>
        )}
      </div>
      {adding && (
        <div className="flex gap-2 mt-2">
          <input className="input flex-1" value={other} placeholder={placeholder} autoFocus
            onChange={(e) => setOther(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOther(); } }} />
          <button type="button" onClick={addOther} aria-label="Add language"
            className="w-[46px] shrink-0 rounded-xl bg-[#7BAE00] text-white text-lg font-bold">+</button>
        </div>
      )}
    </div>
  );
}

/* ── Photos ──────────────────────────────────────────────────────────── */
/* A photo slot: shows the photo once picked, a dashed "tap to add" before. Red only after
   a submit attempt said it is missing - never as the resting state. */
export function PhotoTile({ file, previewUrl, label, onPick, busy, error, tapText, addedText, busyText, tileRef }) {
  return (
    <button type="button" ref={tileRef} onClick={onPick}
      className={`relative w-full aspect-[4/3] rounded-2xl overflow-hidden border-2 transition-all active:scale-[0.98] ${
        error ? "border-[#DC3545] bg-red-50" : file ? "border-[#7BAE00] bg-white" : "border-dashed border-[#CBD5E1] bg-[#F9FAFB]"}`}>
      {file && previewUrl ? (
        <>
          <img src={previewUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#7BAE00] flex items-center justify-center shadow">
            <Icon name="check" size={14} stroke="white" strokeWidth={3} />
          </span>
          <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[10px] font-semibold py-1 text-center">{label} · {addedText}</span>
        </>
      ) : (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-2">
          <span className="w-10 h-10 rounded-full bg-white border border-[#E5E7EB] flex items-center justify-center">
            <Icon name={busy ? "clock" : "camera"} size={18} stroke="#5E8700" />
          </span>
          <span className="text-[11px] font-bold text-[#1F2937] text-center leading-tight">{label}</span>
          <span className="text-[9px] text-[#6B7280]">{busy ? busyText : tapText}</span>
        </span>
      )}
    </button>
  );
}

/* ── Page frame ──────────────────────────────────────────────────────── */
/* Call Sramika support - the number in company settings, the same one the app header's
   Support button dials, reduced to its last ten digits so "+918341265847" and
   "8341265847" both dial. With no usable number it renders nothing rather than a button
   that dials something wrong. */
export function OnbHelpButton({ T, bordered = false }) {
  const cfg = usePlatformConfig();
  const digits = String(cfg?.supportPhoneNumber || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10 || digits === "9999999999") return null;
  return (
    <a href={`tel:+91${digits}`} aria-label={T("help_call")}
      className={`shrink-0 h-8 pl-2 pr-2.5 rounded-full bg-white text-[#3F5E00] flex items-center gap-1 shadow active:scale-95 ${bordered ? "border border-[#CFE3A0]" : ""}`}>
      <Icon name="phone" size={14} stroke="#5E8700" />
      <span className="text-[11px] font-bold leading-none">{T("help_call")}</span>
    </a>
  );
}

/* The green header - one short row: back, who this form is for, and help. Kept low so
   the form starts on the first screen of a phone. */
export function OnbHeader({ kicker, title, art, onBack, T }) {
  return (
    <div className="relative w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #7BAE00 0%, #5E8700 100%)" }}>
      {art && (
        <img src={art} alt="" aria-hidden="true"
          className="absolute right-0 top-0 h-full w-[40%] object-cover opacity-60"
          style={{ WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 70%)", maskImage: "linear-gradient(to right, transparent 0%, #000 70%)" }} />
      )}
      <div className="relative flex items-center gap-2.5 px-3 py-2.5 max-w-5xl lg:max-w-[1100px] mx-auto">
        <button type="button" onClick={onBack} aria-label="Go back"
          className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0 active:scale-95">
          <Icon name="arrowLeft" size={18} stroke="white" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] text-white/85 uppercase tracking-widest font-bold leading-tight truncate">{kicker}</p>
          <h1 className="text-[15px] font-extrabold text-white leading-tight line-clamp-2">{title}</h1>
        </div>
        <OnbHelpButton T={T} />
      </div>
    </div>
  );
}

/* Where you are: "Step 3 of 9" and one circle per section - ticked when done, filled for
   the one to do next. Sticks to the top while scrolling; a circle takes you there. */
export function StepRail({ order, done, current, onGo, T }) {
  const total = order.length;
  const currentIdx = Math.max(0, order.indexOf(current));
  return (
    <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#EEF2F7] px-3 py-2">
      <div className="flex items-center gap-2 max-w-5xl lg:max-w-[1100px] mx-auto">
        <span className="text-[11px] font-bold text-[#1F2937] shrink-0">{T("step_of", { n: current ? currentIdx + 1 : total, total })}</span>
        <div className="flex items-center flex-1 min-w-0">
          {order.map((sec, i) => {
            const isDone = !!done[sec];
            const isNow = sec === current;
            return (
              <div key={sec} className="flex items-center flex-1 last:flex-none min-w-0">
                <button type="button" onClick={() => onGo(sec)} aria-label={`${i + 1}`}
                  className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-extrabold transition-all ${
                    isDone ? "bg-[#7BAE00] text-white" : isNow ? "bg-[#5E8700] text-white ring-2 ring-[#7BAE00]/30" : "bg-[#E5E7EB] text-[#6B7280]"}`}>
                  {isDone ? <Icon name="check" size={12} stroke="white" strokeWidth={3} /> : i + 1}
                </button>
                {i < total - 1 && <span className={`h-[2px] flex-1 mx-0.5 ${isDone ? "bg-[#7BAE00]" : "bg-[#E5E7EB]"}`} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* The one button: always Submit. Above it, in words, what is next and how far along -
   a submit with gaps takes the reader to the first one. */
export function OnbStickyBar({ allDone, doneCount, total, nextTitle, submitting, submitStep, T, formId }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-[#E5E7EB] px-3 pt-2"
      style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" }}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between text-[10px] font-semibold text-[#6B7280] mb-1.5 px-0.5">
          <span>{allDone ? T("prog_all") : T("next_to", { section: nextTitle })}</span>
          <span className="text-[#5E8700]">{T("prog_done", { done: doneCount, total })}</span>
        </div>
        <button type="submit" form={formId} disabled={submitting}
          className="onb-btn w-full h-12 rounded-2xl bg-[#7BAE00] hover:bg-[#689300] text-white text-[15px] font-bold flex items-center justify-center gap-2 shadow-md active:scale-[0.99] disabled:opacity-60">
          {submitting ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
              {submitStep || T("sub_upload")}
            </>
          ) : (
            <>{T("btn_submit")} <Icon name="arrowRight" size={16} stroke="white" /></>
          )}
        </button>
      </div>
    </div>
  );
}

/* Toast - sits above the sticky bar, so it is never hidden behind it. */
export function OnbToast({ visible, msg, onClose }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`fixed bottom-[104px] left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-sm
        bg-red-600 text-white text-[11px] font-semibold px-4 py-3 rounded-xl shadow-xl
        flex items-center gap-2 transition-all duration-300
        ${visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}`}>
      <Icon name="alertTri" size={16} stroke="white" />
      <span className="flex-1 leading-snug">{msg}</span>
      <button type="button" onClick={onClose} aria-label="Close" className="shrink-0 ml-1 text-white/80 hover:text-white">
        <Icon name="x" size={14} stroke="white" />
      </button>
    </div>
  );
}

/* The saved application, picked up again. Shown after OTP when a draft was found and
   applied: says so, with the date, and offers to start again instead. */
export function ResumedBanner({ date, T, onStartFresh }) {
  return (
    <div className="bg-[#F6FAEC] border border-[#7BAE00]/30 rounded-2xl p-3 fade-up">
      <div className="flex items-start gap-2">
        <Icon name="checkCircle" size={16} stroke="#5E8700" className="shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold text-[#3F5E00]">{T("resumed_from", { date })}</p>
          <p className="text-[10px] text-[#5E8700] mt-0.5">{T("resumed_sub")}</p>
        </div>
      </div>
      <button type="button" onClick={onStartFresh}
        className="mt-2 w-full h-9 rounded-xl text-[12px] font-bold text-[#5E8700] bg-white border border-[#7BAE00]/40">
        {T("btn_fresh")}
      </button>
    </div>
  );
}

/* ── Working days and hours ──────────────────────────────────────────────
   Most partners keep the default - every day, all day - so the week is three plain rows:
   all day, 5 AM–11 PM, or choose; the seven-day table opens only for the third. */
export function ScheduleCard({ weeklySchedule, is24x7, is5to11, allDaysEnabled, applyPreset, toggleAllDays, toggleDay, setDayTime, showSchedule, setShowSchedule, T }) {
  const custom = !is24x7 && !is5to11;
  const enabledDays = Object.values(weeklySchedule).filter((d) => d.enabled).length;
  return (
    <div className="space-y-2">
      <ChoiceRow on={is24x7 && !showSchedule} onClick={() => { applyPreset("00:00", "23:59"); setShowSchedule(false); }} icon="clock" title={T("sched_247")} />
      <ChoiceRow on={is5to11 && !showSchedule} onClick={() => { applyPreset("05:00", "23:00"); setShowSchedule(false); }} icon="sun" title={T("sched_5to11")} />
      <ChoiceRow on={showSchedule || custom} onClick={() => setShowSchedule(true)} icon="calendar"
        title={T("sched_choose")} sub={custom ? T("sched_custom", { n: enabledDays }) : null} />

      {showSchedule && (
        <div className="mt-1 space-y-1.5 rounded-xl border border-[#EEF2F7] bg-[#F9FAFB] p-2">
          <div className="flex justify-between items-center">
            <Pill on={allDaysEnabled} onClick={toggleAllDays} className="!min-h-[32px] !py-1">{T("all_days")}</Pill>
            <button type="button" onClick={() => setShowSchedule(false)} className="text-[12px] font-bold text-[#5E8700] px-2">{T("btn_times_done")}</button>
          </div>
          {Object.entries(weeklySchedule).map(([day, cfg]) => (
            <div key={day} className={`rounded-xl p-2 ${cfg.enabled ? "bg-white border border-[#E5E7EB]" : "bg-[#F3F4F6] border border-dashed border-[#CBD5E1]"}`}>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => toggleDay(day)}
                  aria-label={T(`day_${day}`)}
                  className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${cfg.enabled ? "bg-[#7BAE00] border-[#7BAE00]" : "bg-white border-[#CBD5E1]"}`}>
                  {cfg.enabled && <Icon name="check" size={13} stroke="white" strokeWidth={3} />}
                </button>
                <span className={`w-12 text-[12px] font-bold ${cfg.enabled ? "text-[#1F2937]" : "text-[#9CA3AF]"}`}>{T(`day_${day}`)}</span>
                {cfg.enabled ? (
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <input type="time" value={cfg.start} aria-label={T("col_from")}
                      onChange={(e) => setDayTime(day, "start", e.target.value)}
                      className="flex-1 min-w-0 h-9 text-[12px] px-1.5 rounded-lg border border-[#E5E7EB] bg-white text-center" />
                    <span className="text-[10px] text-[#9CA3AF]">–</span>
                    <input type="time" value={cfg.end} aria-label={T("col_to")}
                      onChange={(e) => setDayTime(day, "end", e.target.value)}
                      className="flex-1 min-w-0 h-9 text-[12px] px-1.5 rounded-lg border border-[#E5E7EB] bg-white text-center" />
                  </div>
                ) : (
                  <span className="flex-1 text-[11px] text-[#9CA3AF] italic">{T("day_off")}</span>
                )}
              </div>
              {cfg.enabled && (
                <div className="flex gap-1.5 mt-1.5 pl-8">
                  {[["Available", "st_available", "#15803D", "#DCFCE7"], ["Busy", "st_busy", "#B45309", "#FEF3C7"], ["Unavailable", "st_unavailable", "#B91C1C", "#FEE2E2"]].map(([v, k, fg, bg]) => {
                    const on = (cfg.status || "Available") === v;
                    return (
                      <button key={v} type="button" onClick={() => setDayTime(day, "status", v)}
                        className="px-2 py-1 rounded-full text-[10px] font-bold border"
                        style={on ? { color: fg, background: bg, borderColor: fg } : { color: "#6B7280", background: "white", borderColor: "#E5E7EB" }}>
                        {T(k)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* A "+91" mobile number box with the flag - the same for the login number and the
   emergency one. */
export function PhoneBox({ value, onChange, onBlur, disabled, placeholder, bad, boxRef }) {
  return (
    <div ref={boxRef} className={`flex items-center border rounded-xl overflow-hidden bg-white ${bad ? "border-[#DC3545]" : "border-[#E5E7EB]"}`}>
      <span className="px-3 h-[46px] flex items-center gap-2 text-[13px] font-semibold text-[#374151] bg-[#F9FAFB] border-r border-[#E5E7EB] shrink-0">
        <IndiaFlag /> +91
      </span>
      <input className="flex-1 min-w-0 px-3 h-[46px] text-[15px] bg-transparent focus:outline-none tracking-wide" inputMode="numeric" type="tel"
        maxLength={10} value={value} placeholder={placeholder} disabled={disabled}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))} onBlur={onBlur} />
    </div>
  );
}
